import ApplicationController from './application_controller'
import { createPopper } from '@popperjs/core'

// TODO: Overflow results are scrolled, and the highlight box can go beyond what is visible. Consider making move*() methods
//       call scrollTo() inside of search results box (tried this and failed)

// This controller manages turbo-frame based search results with several helpful features:
//    - disables item targets that are already in selectedItemTargets
//    - tab/shift+tab/arrow key navigation of items
//    - "click" behavior on enter key
//    - visible focused item with enter key hint.
//		- popper initialization
//
export default class extends ApplicationController {

	static targets = [
		'input',
		'popperElement',
		'searchResults',
		'searchResultsTurboFrame',
		'item',
		'selectedItem',
		'searchParameter',
	]

	//TODO: refactor disableItemsInList calls in codebase to use selectedItem Targets
	static values = {
		disableItemsInList: { type: String, default: '' },
		queryParams: { type: Array, default: [] },
		searchPath: String,
		closeOnSelect: Boolean,
	}

	static outlets = [ 'searchable-param' ]

	connect() {
		this.disabledItemIds = []
		this.initPopper()
	}

	itemTargetConnected(itemTarget) {
		if (this.selectedItemTargets.some(selectedItem => { 
			const presentInSelectedItems = selectedItem.dataset.domId == itemTarget.dataset.domId
			const markedForDestruction = selectedItem.classList.contains('d-none')
			return presentInSelectedItems && !markedForDestruction
		})) {
			this.disableItem(itemTarget)
		}

		this.resetHighlight()
	}

	disableItem(item) {
		item.classList.add('disabled')
		this.searchResultsTarget.append(item)
	}
	
	initPopper() {
		this.popper = createPopper(this.inputTarget, this.popperElementTarget )
	}
	
	processKeyActions(event) {
		if (event.keyCode == 13) { // enter
			event.preventDefault()
			const highlightedItem = this.itemTargets.find( item => item.classList.contains('highlight'))
			if (highlightedItem && !highlightedItem.classList.contains('disabled')) {
				this.selectHighlightedItem()
				this.resetSearch()
				if (this.hasCloseOnSelectValue && this.closeOnSelectValue) {
					this.inputTarget.blur()
				}
			}
		}
		if (event.keyCode == 40 || (!event.shiftKey && event.keyCode == 9)) { // down arrow or tab
			event.preventDefault()
			this.moveHighlightDown()
		}
		if (event.keyCode == 38 || (event.shiftKey && event.keyCode == 9)) { // up arrow or shift+tab
			event.preventDefault()
			this.moveHighlightUp()
		}
		if (event.keyCode == 27) { // escape
			event.preventDefault()
			this.inputTarget.blur()
		}
	}

	selectHighlightedItem() {
		const highlightedItem = this.itemTargets.find( item => item.classList.contains('highlight') )
		if (highlightedItem && highlightedItem.classList.contains('disabled')) {
			return
		} else if (highlightedItem) {
			highlightedItem.click()
		} else {
			this.itemTargets[0].click()
		}
	}

	resetHighlight() {
		if (!this.hasItemTarget) return undefined
		const highlightedItem = this.itemTargets.find( item => item.classList.contains('highlight') )
		if (highlightedItem) this.removeHighlightFromItem(highlightedItem)
		this.addHighlightToItem(this.itemTargets[0])
	}

	moveHighlightDown() {
		if (!this.hasItemTarget) return undefined
		const highlightedItem = this.itemTargets.find( item => item.classList.contains('highlight') )
		if (highlightedItem) {
			const index = this.itemTargets.indexOf(highlightedItem)
			if (index < (this.itemTargets.length - 1)) {
				this.removeHighlightFromItem(highlightedItem)
				this.addHighlightToItem(this.itemTargets[index + 1])
			}
		} else {
			this.itemTargets[0].classList.add('highlight')
		}
	}

	moveHighlightUp() {
		if (!this.hasItemTarget) return undefined
		const highlightedItem = this.itemTargets.find( item => item.classList.contains('highlight') )
		if (highlightedItem) {
			const index = this.itemTargets.indexOf(highlightedItem)
			if (index > 0) {
				this.removeHighlightFromItem(highlightedItem)
				this.addHighlightToItem(this.itemTargets[index - 1])
			}
		} else {
			this.itemTargets[0].classList.add('highlight')
		}
	}

	addHighlightToItem(element) {
		element.classList.add('highlight')
		if (!element.classList.contains('disabled')) {
			element.querySelector('div.hint-enter-to-add')?.classList?.remove('d-none')
		}
	}

	removeHighlightFromItem(element) {
		element.classList.remove('highlight')
		element.querySelector('div.hint-enter-to-add')?.classList?.add('d-none')
	}

	updateSearch(_event) {
		clearTimeout(this.searchDelayTimer)
		this.searchDelayTimer = setTimeout( () => { this.performSearch() }, 250 )
	}

	performSearch() {
		this.searchResultsTurboFrameTarget.src = `${this.searchPathValue}?${this.buildQueryString()}`
		this.searchResultsTurboFrameTarget.reload()
	}

	buildQueryString() {
		const queryFragments = [`search=${this.inputTarget.value}`]

		this.queryParamsValue.map((element) => {
			const parameter = element[0]
			const value = element[1]
			return `${parameter}=${value}`
		}).forEach((fragment) => queryFragments.push(fragment))

		this.searchParameterTargets.map((searchParameter) => {
			if (searchParameter.type == 'checkbox' && !searchParameter.checked) {
				return null
			} else {  
				const parameter = searchParameter.name
				const value = searchParameter.value
				return `${parameter}=${value}`
			}
		})
		.filter((fragment) => fragment != null)
		.forEach((fragment) => queryFragments.push(fragment))

		this.searchableParamOutlets.map((searchableParamController) => {
			const parameter = searchableParamController.getName()
			const value = searchableParamController.getValue()
			return value == null ? null : `${parameter}=${value}`
		})
		.filter((fragment) => fragment != null)
		.forEach((fragment) => queryFragments.push(fragment))

		return queryFragments.join('&')
	}

	selectFirstItem() {
		this.itemTargets[0].click()
	}

	resetSearch(_event) {
		this.inputTarget.value = ''
		if (this.hasSearchResultsTarget) {
			this.searchResultsTarget.innerHTML = "<li class='list-group-item text-600 search-result'>Type to Begin Searching</li>"
		}
	}

	inputFocused(_event) {
		this.popperElementTarget.classList.remove('d-none')
		this.popperElementTarget.setAttribute('data-show', '')
		this.popper.update();
	}

	inputBlurred(event) {
		this.popperElementTarget.classList.add('d-none')
		this.popperElementTarget.removeAttribute('data-show')
	}

	clickItem(event) {
		event.currentTarget.click()
	}

}

